import { FC, useEffect, useMemo, useState } from "react";
import { Box, Stack, Text } from "@chakra-ui/react";
import { NumberFormatValues, NumericFormat } from "react-number-format";

import { useDebounce } from "../../../../hooks";
import { useGetLimitQuery } from "../../../../../../api/limit";
import { InputWithCurrency } from "../../../../../shared/Inputs";
import { useSbpTransferStore } from "../../../../../../store/sbpTransferStore.ts";
import { formatPriceWithoutZeros, isStringWithNumber } from "../../../../../../utils";

export const MIN_SBP_TRANSFER_AMOUNT = 100;
const MAX_SBP_TRANSFER_AMOUNT = 99999999; // 99млн

export const SbpTransferPageAmount: FC = () => {
  const { data } = useGetLimitQuery();
  const { amount, setAmount } = useSbpTransferStore(state => state);

  const [localAmount, setLocalAmount] = useState<string>(amount);
  const debouncedAmount = useDebounce(localAmount, 800);

  const [isError, setIsError] = useState<boolean>(false);
  const availableAmount = isStringWithNumber(data?.cashBalance) ? Number(data?.cashBalance) : 0;

  const message = useMemo(() => {
    let result = `Минимальная сумма для перевода ${formatPriceWithoutZeros(MIN_SBP_TRANSFER_AMOUNT)}`;

    if (localAmount && Number(localAmount) > availableAmount) {
      result = `Максимальная сумма для перевода ${formatPriceWithoutZeros(availableAmount)}`;
    }

    return result;
  }, [localAmount, availableAmount]);

  useEffect(() => {
    if (!debouncedAmount) {
      setAmount("");
      return;
    }

    setAmount(debouncedAmount);
  }, [debouncedAmount, availableAmount]);

  useEffect(() => {
    if (localAmount && (Number(localAmount) > availableAmount)) {
      setIsError(true);
    } else {
      setIsError(false);
    }
  }, [localAmount]);

  const validateValue = (values: NumberFormatValues) => {
    const { floatValue } = values;
    return floatValue === undefined || floatValue <= MAX_SBP_TRANSFER_AMOUNT;
  };

  const onChange = (newAmount: string) => {
    setLocalAmount(newAmount);
  };

  const onBlur = () => {
    if (localAmount && (Number(localAmount) < MIN_SBP_TRANSFER_AMOUNT)) {
      setIsError(true);
    }
  };

  return (
    <Box w="100%" mb="13px">
      <Stack h="40px" px="15px" border="1px solid" borderRadius="12px" borderColor={isError ? "red.500" : "purple.500"}>
        <NumericFormat
          customInput={InputWithCurrency}
          value={localAmount}
          onValueChange={(values, sourceInfo) => {
            if (sourceInfo.source as string === "event") {
              onChange(values.value);
            }
          }}
          onBlur={onBlur}
          isAllowed={validateValue}
          decimalSeparator=","
          decimalScale={2}
          fixedDecimalScale={false}
          allowNegative={false}
          allowLeadingZeros={false}
          thousandSeparator=" "
            // For InputWithCurrency
          color="brand.500"
          fontSize="20px"
          placeholder="Введите сумму перевода"
        />
      </Stack>

      <Text variant={isError ? "red" : "gray"} fontSize="12.5px" mt="2px">
        {message}
      </Text>
    </Box>
  );
};
